// PO10 (docs/build-briefs/PO10-vendor-price-list-import.md; DEC-294): the logic of
// the "Update prices from a file" screen that needs no DOM, so it is unit tested
// on its own (test/po10-vendor-price-list-model.test.ts). The server computes
// every row (matching, cents, percent, the held flag); this file only chooses
// what to show: tabs, filters, sort, the Update or Skip state, the counts the
// confirm sheet reads, and the words for each refusal.

import type {
  ApiErrorBody,
  PriceListChangedSinceCheckDto,
  PriceListIgnoredRowDto,
  PriceListPreviewDto,
  PriceListRowDto,
} from "./api.ts";
import { formatCents } from "./money.ts";

export type Tab = "changes" | "skufill" | "nochange" | "problems" | "notinfile" | "nocode" | "ignored";
export type ChangeFilter = "all" | "up" | "down" | "check";
export type ChangeSort = "check" | "name";

/** The page size of every long list: 25 rows, then "Show 25 more". */
export const PAGE_SIZE = 25;

export const TABS: ReadonlyArray<{ id: Tab; label: string }> = [
  { id: "changes", label: "Price changes" },
  { id: "skufill", label: "NC codes to add" },
  { id: "nochange", label: "No change" },
  { id: "problems", label: "Problems" },
  { id: "notinfile", label: "Not in the file" },
  { id: "nocode", label: "No NC code" },
  { id: "ignored", label: "Not in your items" },
];

export function tabCount(preview: PriceListPreviewDto, tab: Tab): number {
  const s = preview.summary;
  switch (tab) {
    case "changes":
      return s.changes;
    case "skufill":
      return s.skuFill ?? 0;
    case "nochange":
      return s.noChange;
    case "problems":
      return s.problems;
    case "notinfile":
      return s.notInFile;
    case "nocode":
      return s.noCode;
    case "ignored":
      return s.ignored;
  }
}

export function plural(count: number, one: string, many: string): string {
  return `${count.toLocaleString("en-US")} ${count === 1 ? one : many}`;
}

// ---------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------

export function rowsInGroup(
  preview: PriceListPreviewDto,
  group: PriceListRowDto["group"],
): PriceListRowDto[] {
  return preview.rows.filter((row) => row.group === group);
}

export function direction(row: PriceListRowDto): "up" | "down" | "first" | "flat" {
  if (row.firstPrice) return "first";
  const tenths = row.changePercentTenths;
  if (tenths === null || tenths === 0) return "flat";
  return tenths > 0 ? "up" : "down";
}

/** "+30.8%", "-2.5%", "0.0%". Tenths arrive as whole numbers from the server. */
export function percentText(tenths: number): string {
  const sign = tenths > 0 ? "+" : tenths < 0 ? "-" : "";
  return `${sign}${(Math.abs(tenths) / 10).toFixed(1)}%`;
}

/** The Check first line under a held row: "Check first: this is a 30.8% jump, held back". */
export function heldFlagText(row: PriceListRowDto): string {
  const tenths = Math.abs(row.changePercentTenths ?? 0);
  const word = direction(row) === "down" ? "drop" : "jump";
  return `Check first: this is a ${(tenths / 10).toFixed(1)}% ${word}, held back`;
}

export function priceOrNone(cents: number | null): string {
  return cents === null ? "No price" : formatCents(cents);
}

export function filterChanges(rows: PriceListRowDto[], filter: ChangeFilter): PriceListRowDto[] {
  switch (filter) {
    case "all":
      return rows;
    case "up":
      return rows.filter((row) => direction(row) === "up");
    case "down":
      return rows.filter((row) => direction(row) === "down");
    case "check":
      return rows.filter((row) => row.held);
  }
}

export function sortChanges(rows: PriceListRowDto[], sort: ChangeSort): PriceListRowDto[] {
  const byName = (a: PriceListRowDto, b: PriceListRowDto): number =>
    a.productName.localeCompare(b.productName) || (a.offerId < b.offerId ? -1 : 1);
  const copy = [...rows];
  if (sort === "name") {
    return copy.sort(byName);
  }
  // Check first, then the biggest move in either direction, then the name.
  return copy.sort((a, b) => {
    if (a.held !== b.held) return a.held ? -1 : 1;
    const size = Math.abs(b.changePercentTenths ?? 0) - Math.abs(a.changePercentTenths ?? 0);
    if (size !== 0) return size;
    return byName(a, b);
  });
}

// ---------------------------------------------------------------------
// The Update or Skip state
// ---------------------------------------------------------------------

export interface Choices {
  /** Non-held change rows the user switched to Skip. */
  skipped: ReadonlySet<string>;
  /** Held rows the user switched to Update. */
  optedIn: ReadonlySet<string>;
  /** DEC-451: item id to the NC code switched on for it. An item not in the map is off. */
  fillCodes: ReadonlyMap<string, string>;
}

/** The choices a fresh check opens with: every price on, held ones off, and each NC code the server marked ready on. */
export function initialChoices(preview: PriceListPreviewDto): Choices {
  const fillCodes = new Map<string, string>();
  for (const row of rowsInGroup(preview, "sku_fill")) {
    const first = row.skuFill?.candidates[0];
    if (row.skuFill?.defaultOn === true && first !== undefined) {
      fillCodes.set(row.productId, first.ncCode);
    }
  }
  return { skipped: new Set(), optedIn: new Set(), fillCodes };
}

/** Switch an NC code on for an item (a code), or off (null). */
export function setFill(choices: Choices, productId: string, ncCode: string | null): Choices {
  const fillCodes = new Map(choices.fillCodes);
  if (ncCode === null) {
    fillCodes.delete(productId);
  } else {
    fillCodes.set(productId, ncCode);
  }
  return { skipped: choices.skipped, optedIn: choices.optedIn, fillCodes };
}

/** True when two items are switched on for the same file row, which the server refuses. */
export function fillCodeInUseElsewhere(choices: Choices, productId: string, ncCode: string): boolean {
  for (const [other, code] of choices.fillCodes) {
    if (other !== productId && code === ncCode) return true;
  }
  return false;
}

export function isOnUpdate(row: PriceListRowDto, choices: Choices): boolean {
  return row.held ? choices.optedIn.has(row.offerId) : !choices.skipped.has(row.offerId);
}

export function toggle(row: PriceListRowDto, choices: Choices): Choices {
  const skipped = new Set(choices.skipped);
  const optedIn = new Set(choices.optedIn);
  const target = row.held ? optedIn : skipped;
  if (target.has(row.offerId)) {
    target.delete(row.offerId);
  } else {
    target.add(row.offerId);
  }
  return { skipped, optedIn, fillCodes: choices.fillCodes };
}

export interface SelectionCounts {
  updating: number;
  up: number;
  down: number;
  firstPrice: number;
  /** Non-held rows switched to Skip. */
  skippedByUser: number;
  /** Held rows left on Skip. */
  heldBack: number;
  problems: number;
  /** DEC-451: NC codes switched on. */
  skuFills: number;
}

/** Counts only: no sum or comparison of prices is made on the client. */
export function selectionCounts(preview: PriceListPreviewDto, choices: Choices): SelectionCounts {
  const counts: SelectionCounts = {
    updating: 0,
    up: 0,
    down: 0,
    firstPrice: 0,
    skippedByUser: 0,
    heldBack: 0,
    problems: preview.summary.problems,
    skuFills: choices.fillCodes.size,
  };
  for (const row of rowsInGroup(preview, "change")) {
    if (isOnUpdate(row, choices)) {
      counts.updating += 1;
      const d = direction(row);
      if (d === "up") counts.up += 1;
      else if (d === "down") counts.down += 1;
      else if (d === "first") counts.firstPrice += 1;
    } else if (row.held) {
      counts.heldBack += 1;
    } else {
      counts.skippedByUser += 1;
    }
  }
  return counts;
}

export function applyBody(
  preview: PriceListPreviewDto,
  choices: Choices,
  idempotencyKey: string,
): {
  previewToken: string;
  idempotencyKey: string;
  excludedOfferIds: string[];
  includedHeldOfferIds: string[];
  skuFills: Array<{ productId: string; ncCode: string }>;
} {
  const rows = rowsInGroup(preview, "change");
  return {
    previewToken: preview.previewToken,
    idempotencyKey,
    excludedOfferIds: rows
      .filter((r) => !r.held && choices.skipped.has(r.offerId))
      .map((r) => r.offerId),
    includedHeldOfferIds: rows
      .filter((r) => r.held && choices.optedIn.has(r.offerId))
      .map((r) => r.offerId),
    skuFills: [...choices.fillCodes].map(([productId, ncCode]) => ({ productId, ncCode })),
  };
}

// ---------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------

export function fileSizeText(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function dateText(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function dateTimeText(iso: string): string {
  const date = new Date(iso);
  const time = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${dateText(iso)} at ${time}`;
}

/** The short price list value on a vendor row: "Prices: Sep 28" (the year only when it is not this one), or
 * "No prices loaded" when no list was ever loaded. */
export function priceStatusText(
  importRecord: { createdAt: string } | null,
  now: Date = new Date(),
): string {
  if (importRecord === null) {
    return "No prices loaded";
  }
  const date = new Date(importRecord.createdAt);
  const sameYear = date.getFullYear() === now.getFullYear();
  const text = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
  return `Prices: ${text}`;
}

/** DEC-451: the one price the import reads. */
export const PRICE_COLUMN_LABEL = "Mixed Beverage Price";

export function problemRowDetail(row: PriceListRowDto): string[] {
  const chips: string[] = [`NC ${row.ncCode}`];
  row.fileRowNumbers.forEach((rowNumber, index) => {
    const text = row.filePriceTexts[index] ?? "";
    chips.push(`Row ${rowNumber.toLocaleString("en-US")}: ${text === "" ? "blank" : text}`);
  });
  if (row.problem?.code === "order_unit_not_bottle" || row.problem?.code === "size_mismatch") {
    if (row.fileSizeLabel !== null) chips.push(`File size: ${row.fileSizeLabel}`);
    if (row.itemSizeLabel !== null) chips.push(`Item size: ${row.itemSizeLabel}`);
  }
  if (row.problem?.code === "order_unit_not_bottle" && row.filePriceCents !== null) {
    chips.push(`File price: ${formatCents(row.filePriceCents)} a bottle`);
  }
  chips.push(`Price now: ${priceOrNone(row.currentCostCents)}`);
  return chips;
}

export function staleLine(entry: PriceListChangedSinceCheckDto): string {
  const now =
    entry.reason === "deactivated"
      ? "no longer active"
      : entry.reason === "code_changed"
        ? "its NC code was changed"
        : entry.reason === "unit_changed"
          ? "its order unit was changed"
          : entry.reason === "offer_added"
            ? "it now has an offer"
            : entry.reason === "code_added"
              ? "it now has an NC code"
              : entry.reason === "code_taken"
                ? `NC ${entry.ncCode} now belongs to another item`
                : `now ${priceOrNone(entry.currentCostCents)}`;
  return entry.newCostCents === null
    ? `${entry.productName}: ${now}, file says NC ${entry.ncCode}`
    : `${entry.productName}: ${now}, file says ${formatCents(entry.newCostCents)}`;
}

export function ignoredLine(row: PriceListIgnoredRowDto, priceLabel: string): string {
  const price =
    row.newCostCents === null
      ? "no usable price"
      : `${priceLabel} ${formatCents(row.newCostCents)}`;
  return `NC ${row.ncCode}. ${price}.`;
}

// ---------------------------------------------------------------------
// Refusals: what happened, whether anything saved, and the next tap
// ---------------------------------------------------------------------

export type ErrorKind =
  | "unreadable"
  | "toobig"
  | "nocode"
  | "noprice"
  | "expired"
  | "used"
  | "network"
  | "denied"
  | "novendor"
  | "other";

export interface ErrorView {
  kind: ErrorKind;
  title: string;
  message: string;
  /** The column headings the file did have, for the two column refusals. */
  columns: string[];
}

export function errorView(
  status: number,
  networkError: boolean,
  body: (Partial<ApiErrorBody> & { found?: string[] }) | undefined,
  step: "choose" | "update",
): ErrorView {
  const code = body?.error;
  const view = (kind: ErrorKind, title: string, message: string): ErrorView => ({
    kind,
    title,
    message,
    columns: body?.found ?? [],
  });
  if (networkError || status === 0) {
    return view(
      "network",
      step === "update" ? "The update is not confirmed" : "We could not reach the server",
      step === "update"
        ? "The connection dropped before the server answered, so the prices may or may not have changed. Retry to find out: the same update is never applied twice."
        : "Check your connection and try again. Nothing was saved.",
    );
  }
  if (status === 401 || status === 403) {
    return view(
      "denied",
      "You are not allowed to update price lists",
      "Ask an owner or manager to give you the Update vendor price lists permission. Nothing was changed.",
    );
  }
  switch (code) {
    case "price_list_unsupported_type":
    case "price_list_unreadable":
      return view(
        "unreadable",
        "We could not read this file",
        "It is not an Excel (.xlsx) or CSV file, it is password protected, or it is damaged. Nothing was saved.",
      );
    case "price_list_too_large":
    case "price_list_too_many_rows":
      return view(
        "toobig",
        "This file is too big",
        "Files can be up to 5 MB and 20,000 rows. Nothing was saved.",
      );
    case "price_list_no_code_column":
      return view(
        "nocode",
        "We could not find the NC code column",
        "These are the columns we found. Tap the one that holds the NC code.",
      );
    case "price_list_no_price_column":
      return view(
        "noprice",
        "We could not find the price column",
        "These are the columns we found. Tap the one that holds the price for one bottle.",
      );
    case "price_list_preview_expired":
      return view(
        "expired",
        "This check ran out of time",
        "A check is good for 30 minutes. This one is older, so prices may have moved. Nothing was saved.",
      );
    case "price_list_preview_used":
    case "price_list_preview_not_found":
      return view(
        "used",
        "This check can no longer be used",
        "It was already used or it is gone. Choose the file again to check it afresh. Nothing was saved.",
      );
    case "vendor_not_price_list_vendor":
      return view(
        "novendor",
        "This vendor cannot take a price list",
        "Price lists load only for an active vendor whose Price file layout is NC ABC. Set it in Settings, Vendors. Nothing was saved.",
      );
    default:
      return view(
        "other",
        "The update did not go through",
        `${body?.message ?? "Something went wrong."} Nothing was saved.`,
      );
  }
}

/** DOCSBUG7: where the screen was opened from, which decides the path shown above
 * its title. UX80 (DEC-298) gave it two homes: the account menu's Ordering group
 * and a vendor card in Settings, Vendors. */
export type PriceListOrigin = "menu" | "settings";

/** The path above the title, written as the person walked it. */
export function priceListCrumb(origin: PriceListOrigin, vendorName: string): string {
  return origin === "menu"
    ? `Menu, Ordering, Update prices from a file, ${vendorName}`
    : `Settings, Vendors, ${vendorName}`;
}
